import React, { useState } from 'react';

export default function PersonalInfoCard({
  fullName = '',
  loftName = '',
  emailAddress = '',
  mobile = '',
  roles = [],
  notificationMethods = [],
  hideContactInfo = false,
  onEdit,
  editable = false,
  onChange,
  editedData = {},
  validationErrors = {},
  originalEmailAddress = '',
  originalMobile = '',
}) {
  const handleChange = (field, value) => {
    if (onChange) {
      onChange(field, value);
    }
  };

  const handleNotifChange = (method) => {
    const currentMethods = editedData.preferredNotificationMethod || [];
    const methods = currentMethods.includes(method)
      ? currentMethods.filter((m) => m !== method)
      : [...currentMethods, method];
    handleChange('preferredNotificationMethod', methods);
  };

  const handleRoleChange = (role) => {
    const currentRoles = Array.isArray(editedData.role) ? editedData.role : [editedData.role];
    const newRoles = currentRoles.includes(role)
      ? currentRoles.filter((r) => r !== role)
      : [...currentRoles, role];
    handleChange('role', newRoles);
  };

  return (
    <div className="card shadow-sm mb-4 position-relative">
      <div className="card-header bg-white py-3">
        <h5 className="mb-0" style={{ color: '#0b3d91' }}>
          <i className="bi bi-person-circle me-2"></i>
          Personal Information
        </h5>
      </div>
      <div className="card-body">
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label fw-bold">Full Name</label>
            {editable ? (
              <input
                type="text"
                className="form-control"
                value={editedData.fullName || ''}
                onChange={e => handleChange('fullName', e.target.value)}
              />
            ) : (
              <input type="text" className="form-control" value={fullName} disabled />
            )}
          </div>
          <div className="col-md-6">
            <label className="form-label fw-bold">Loft Name</label>
            {editable ? (
              <input
                type="text"
                className="form-control"
                value={editedData.loftName || ''}
                onChange={e => handleChange('loftName', e.target.value)}
              />
            ) : (
              <input type="text" className="form-control" value={loftName} disabled />
            )}
          </div>
          <div className="col-md-6">
            <label className="form-label fw-bold">
              Email Address
              <i 
                className="bi bi-question-circle ms-1 text-muted" 
                style={{ cursor: 'help' }}
                title="Contact admin to change email or mobile"
              ></i>
            </label>
            {editable && !originalEmailAddress ? (
              <input
                type="email"
                className={`form-control ${validationErrors?.email ? 'is-invalid' : ''}`}
                value={editedData.emailAddress || ''}
                onChange={e => handleChange('emailAddress', e.target.value)}
                placeholder="Enter email address"
              />
            ) : (
              <input 
                type="email" 
                className="form-control bg-light" 
                value={editable ? (editedData.emailAddress || emailAddress) : emailAddress} 
                disabled 
              />
            )}
            {validationErrors?.email && (
              <div className="text-danger small mt-1">
                <i className="bi bi-exclamation-circle me-1"></i>
                {validationErrors.email}
              </div>
            )}
            {originalEmailAddress && (
              <small className="text-muted">Contact admin to change email</small>
            )}
            {editable && !originalEmailAddress && !validationErrors?.email && (
              <small className="text-muted">Once saved, email cannot be changed</small>
            )}
          </div>
          <div className="col-md-6">
            <label className="form-label fw-bold">
              Mobile
              <i 
                className="bi bi-question-circle ms-1 text-muted" 
                style={{ cursor: 'help' }}
                title="Contact admin to change email or mobile"
              ></i>
            </label>
            {editable && !originalMobile ? (
              <input
                type="tel"
                className={`form-control ${validationErrors?.mobile ? 'is-invalid' : ''}`}
                value={editedData.mobile || ''}
                onChange={e => handleChange('mobile', e.target.value)}
                placeholder="Enter mobile number"
              />
            ) : (
              <input 
                type="tel" 
                className="form-control bg-light" 
                value={editable ? (editedData.mobile || mobile) : mobile} 
                disabled 
              />
            )}
            {validationErrors?.mobile && (
              <div className="text-danger small mt-1">
                <i className="bi bi-exclamation-circle me-1"></i>
                {validationErrors.mobile}
              </div>
            )}
            {originalMobile && (
              <small className="text-muted">Contact admin to change mobile</small>
            )}
            {editable && !originalMobile && !validationErrors?.mobile && (
              <small className="text-muted">Once saved, mobile cannot be changed</small>
            )}
          </div>
          <div className="col-12">
            <label className="form-label fw-bold">Role(s)</label>
            {editable ? (
              <div>
                <div className="d-flex align-items-center gap-3 flex-wrap mb-2">
                  {['Handler', 'Breeder'].map((role) => (
                    <div key={role} className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`role-${role}`}
                        checked={(Array.isArray(editedData.role) ? editedData.role : [editedData.role]).some(r => r && r.toLowerCase() === role.toLowerCase())}
                        onChange={() => handleRoleChange(role)}
                      />
                      <label className="form-check-label" htmlFor={`role-${role}`}>
                        {role}
                      </label>
                    </div>
                  ))}
                </div>
                {/* Show Admin role if present, but not editable */}
                {(Array.isArray(editedData.role) ? editedData.role : [editedData.role]).some(r => r && r.toLowerCase() === 'admin') && (
                  <div className="mt-2">
                    <span className="badge bg-danger fs-6 px-3 py-2">
                      <i className="bi bi-shield-check me-1"></i>
                      Admin (Contact administrator to modify)
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="d-flex align-items-center gap-2 flex-wrap">
                {roles.map((role, idx) => (
                  <span key={idx} className={`badge fs-6 px-3 py-2 ${role && role.toLowerCase() === 'admin' ? 'bg-danger' : 'bg-primary'}`}>
                    {role && role.toLowerCase() === 'admin' && <i className="bi bi-shield-check me-1"></i>}
                    {role}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="col-12 mt-3">
            <label className="form-label fw-bold">Preferred Notification Method</label>
            {editable ? (
              <div>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  {['email', 'text'].map((method) => (
                    <div key={method} className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`notif-${method}`}
                        checked={(editedData.preferredNotificationMethod || []).includes(method)}
                        onChange={() => handleNotifChange(method)}
                      />
                      <label className="form-check-label" htmlFor={`notif-${method}`}>
                        {method.charAt(0).toUpperCase() + method.slice(1)}
                      </label>
                    </div>
                  ))}
                </div>
                {validationErrors?.notification && (
                  <div className="text-danger small mt-2">
                    <i className="bi bi-exclamation-circle me-1"></i>
                    {validationErrors.notification}
                  </div>
                )}
              </div>
            ) : (
              <div className="d-flex align-items-center gap-2 flex-wrap">
                {notificationMethods.length > 0 ? notificationMethods.map((method, idx) => (
                  <span key={idx} className="badge bg-info fs-6 px-3 py-2">
                    {method.charAt(0).toUpperCase() + method.slice(1)}
                  </span>
                )) : (
                  <span className="text-muted">No notification method selected</span>
                )}
              </div>
            )}
          </div>
          <div className="col-12 mt-4">
            <label className="form-label fw-bold">Privacy Settings</label>
            {editable ? (
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="hideContactInfo"
                  checked={editedData.hideContactInfo || false}
                  onChange={e => handleChange('hideContactInfo', e.target.checked)}
                />
                <label className="form-check-label" htmlFor="hideContactInfo">
                  <strong>Hide my contact information from public directory</strong>
                </label>
              </div>
            ) : (
              <div>
                {hideContactInfo ? (
                  <span className="badge bg-warning fs-6 px-3 py-2">
                    <i className="bi bi-eye-slash me-1"></i>
                    Contact info hidden from public
                  </span>
                ) : (
                  <span className="badge bg-success fs-6 px-3 py-2">
                    <i className="bi bi-eye me-1"></i>
                    Contact info visible to public
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
